import React, { useEffect, useState } from 'react'

import { collection, onSnapshot } from 'firebase/firestore'
import { HiOutlinePlus } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'

import { useProfessors, useUser } from '../../../../context'
import { firestore } from '../../../../firebase/firebaseConfig'
import { AdminProfessorMenu, Profesor } from '../../../../types'
import FilteringList from '../../../dropdowns/dropdownList/FilteringList'
import { Spacer } from '../../../global'
import ListWithImage from '../../../lists/listWithImage/ListWithImage'
import Search from '../../../search/Search'
import { SectionSubtitle } from '../../../texts'
import { NewUserButton } from '../styles'

type AdminProfile = {
  uid: string
  name: string
  lastname: string
  image?: string
  status?: string
}

const isVisibleAdmin = (admin: AdminProfile, currentUserId: string) =>
  admin.uid !== currentUserId

const ProfessorsList = () => {
  const navigate = useNavigate()
  const { onEditionMode, user } = useUser()
  const { activeProfessor, professorsList, setActiveProfessor } =
    useProfessors()
  const [searchValue, setSearchValue] = useState<string>('')
  const [list, setList] = useState<Profesor[] | null>(null)
  const [filteredProfessors, setFilterProfessors] = useState<Profesor[] | null>(
    null,
  )
  const [adminList, setAdminList] = useState<AdminProfile[] | null>(null)
  const [adminListError, setAdminListError] = useState<string | null>(null)

  useEffect(() => {
    setFilterProfessors(FilteringList(searchValue, list))
  }, [searchValue, list])

  useEffect(() => {
    if (!user) return undefined

    const adminCollectionRef = collection(firestore, 'admin')

    const unsubscribe = onSnapshot(
      adminCollectionRef,
      (snapshot) => {
        const admins = snapshot.docs.map((adminDocument) => ({
          ...(adminDocument.data() as Omit<AdminProfile, 'uid'>),
          uid: adminDocument.id,
        }))

        setAdminList(
          admins
            .filter((admin) => isVisibleAdmin(admin, user.uid))
            .sort((firstAdmin, secondAdmin) =>
              `${firstAdmin.name} ${firstAdmin.lastname}`.localeCompare(
                `${secondAdmin.name} ${secondAdmin.lastname}`,
                'es',
              ),
            ),
        )
        setAdminListError(null)
      },
      (error) => {
        console.error('No se pudieron cargar los administradores:', error)
        setAdminListError('Hubo un problema al cargar los administradores.')
      },
    )

    return () => unsubscribe()
  }, [user])

  const selectProfessor = (index: number) => {
    if (!onEditionMode) {
      list && setActiveProfessor(list[index])
      navigate(`/profesores/${AdminProfessorMenu.activity.label}`)
    } else {
      list && setActiveProfessor(list[index])
    }
  }

  const selectAdmin = (index: number) => {
    const selectedAdmin = adminList?.[index]

    if (!selectedAdmin || onEditionMode) return
    navigate(`/administradores/${selectedAdmin.uid}`)
  }

  useEffect(() => {
    setList(professorsList)
  }, [professorsList])

  return (
    <div className="list">
      <Spacer height={15} />
      <div style={{ padding: '8px 24px', width: '100%' }}>
        <SectionSubtitle fontWeight={600} fontSize={16} lineHeight={24}>
          Seleccione Profesor
        </SectionSubtitle>
        <Spacer height={10} />
        <Search
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          width="100%"
        />
      </div>
      <Spacer height={10} />
      <ListWithImage
        list={filteredProfessors ? filteredProfessors : list}
        onClick={selectProfessor}
        disabled={onEditionMode}
        activeId={activeProfessor && activeProfessor.uid}
      />
      <NewUserButton $hasSeparation={true}>
        <button
          type="button"
          onClick={() => navigate('/nuevo-usuario?user=professor')}
        >
          <HiOutlinePlus />
        </button>
        <p>Añadir Profesor</p>
      </NewUserButton>
      <NewUserButton $hasSeparation={false}>
        <SectionSubtitle fontSize={14} fontWeight={600}>
          Colaboradores
        </SectionSubtitle>
      </NewUserButton>
      <ListWithImage
        list={adminList}
        onClick={selectAdmin}
        disabled={onEditionMode}
        activeId={null}
      />
      {adminListError && (
        <p role="alert" style={{ padding: '8px 24px' }}>
          No se pudieron cargar los administradores.
        </p>
      )}
      <NewUserButton $hasSeparation={true}>
        <button
          type="button"
          onClick={() => navigate('/nuevo-usuario?user=admin')}
        >
          <HiOutlinePlus />
        </button>
        <p>Añadir Administrador</p>
      </NewUserButton>
    </div>
  )
}

export default ProfessorsList
